/**
 * The value range a session on a track scores on (roadmap S-39, migration 1038).
 *
 * A track keeps a range per criterion (its Criteria & Scales rows); a session
 * scores every criterion on one range (tool_config scoring_scale_min/max). When
 * every ranged criterion on the track shares one whole-number range a person can
 * score on, that is the session's range: the server writes it into each new
 * session on the track and, once the track locks its range, refuses any other.
 * Otherwise this is null and the session picks its own.
 */
import type { ScoringScaleRange } from './toolSettings';

const MAX_SCALE_VALUES = 11;
const WHOLE = /^\d{1,4}$/;

function isRangeObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v) && ('min' in v || 'max' in v || 'type' in v);
}

// reuse-ack: mirrors public.internal_track_scoring_scale (supabase/migrations/1038_a_session_on_a_track_scores_on_its_range.sql) — the server holds the rule; a screen needs the same answer before a session exists, to show it.
export function trackScoringScale(rangeValues: readonly unknown[]): ScoringScaleRange | null {
  const ranged = rangeValues.filter(isRangeObject);
  if (ranged.length === 0) return null;
  let scale: ScoringScaleRange | null = null;
  for (const v of ranged) {
    const type = v.type ?? 'number';
    const lo = String(v.min ?? '');
    const hi = String(v.max ?? '');
    if ((type !== 'number' && type !== 'scale') || !WHOLE.test(lo) || !WHOLE.test(hi)) return null;
    const next = { min: Number(lo), max: Number(hi) };
    if (scale && (scale.min !== next.min || scale.max !== next.max)) return null;
    scale = next;
  }
  if (!scale || scale.max <= scale.min || scale.max - scale.min + 1 > MAX_SCALE_VALUES) return null;
  return scale;
}
